import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BuildIcon from '@mui/icons-material/Build';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { bffNotificationService } from '@resort-os/api-client';
import { Modal } from '@resortifysolutions/modals';
import '@resortifysolutions/modals/index.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const ACCENT = '#2F5D50';
const BORDER = '1px solid #E4E1D8';
const INK = '#1C2321';
const INK_SOFT = '#666F67';
const INK_FAINT = '#96998F';
const SURFACE_SUNK = '#F4F3EE';
const RUST = '#A63D2F';
const RUST_TINT = '#F6E7E3';
const AMBER = '#B07A2C';
const AMBER_TINT = '#F5EDE0';
const BLUE = '#3D6B7D';
const BLUE_TINT = '#E5EEF0';
const PLUM = '#6E4F73';
const PLUM_TINT = '#EFE7F0';
const PINE_TINT = '#E7EFEC';
const GRAY = '#8B9088';
const PANEL_WIDTH = 440;
const MESSAGE_MAX_LENGTH = 160;

interface NotificationCategory {
  color: string;
  tint: string;
  label: string;
  icon: React.ReactNode;
}

const CATEGORIES: Record<string, NotificationCategory> = {
  booking: { color: PLUM, tint: PLUM_TINT, label: 'Front Office', icon: <EventNoteIcon /> },
  payment: { color: ACCENT, tint: PINE_TINT, label: 'Food & Beverage', icon: <AttachMoneyIcon /> },
  maintenance: { color: AMBER, tint: AMBER_TINT, label: 'Maintenance', icon: <BuildIcon /> },
  system: { color: BLUE, tint: BLUE_TINT, label: 'Housekeeping', icon: <InfoOutlinedIcon /> },
  reminder: { color: ACCENT, tint: PINE_TINT, label: 'Reminder', icon: <ScheduleIcon /> },
  alert: { color: RUST, tint: RUST_TINT, label: 'Alert', icon: <WarningAmberIcon /> },
  info: { color: GRAY, tint: SURFACE_SUNK, label: 'Info', icon: <InfoOutlinedIcon /> },
};

function getCategory(type: string): NotificationCategory {
  return CATEGORIES[type] || CATEGORIES.info;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function getDateGroup(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const notifDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  if (notifDate.getTime() === today.getTime()) return 'Today';
  if (notifDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return 'Earlier';
}

function groupByDate(notifications: any[]): { label: string; items: any[] }[] {
  const groups: { label: string; items: any[] }[] = [];
  const map = new Map<string, any[]>();

  for (const n of notifications) {
    const group = getDateGroup(n.created_at);
    if (!map.has(group)) map.set(group, []);
    map.get(group)!.push(n);
  }

  const order = ['Today', 'Yesterday', 'Earlier'];
  for (const label of order) {
    const items = map.get(label);
    if (items && items.length > 0) groups.push({ label, items });
  }

  return groups;
}

const thinScrollbar = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: '#D6D2C6', borderRadius: '8px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#B8B3AD' },
};

interface NotificationModalProps {
  open: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

type Tab = 'inbox' | 'compose';
type Filter = 'all' | 'unread';

export default function NotificationModal({ open, onClose }: NotificationModalProps) {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('inbox');
  const [filter, setFilter] = useState<Filter>('all');
  const [toast, setToast] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [reading, setReading] = useState<any | null>(null);

  const { data: recipientsData } = useQuery({
    queryKey: ['bff-notification-recipients'],
    queryFn: () => bffNotificationService.getRecipients(),
    enabled: open,
  });

  const { data: notifData, isLoading: notifLoading } = useQuery({
    queryKey: ['bff-notifications'],
    queryFn: () => bffNotificationService.list({ page: 1, limit: 50 }),
    refetchInterval: 30000,
    enabled: open,
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      for (const user of selectedUsers) {
        await bffNotificationService.sendToUser({
          to_user_id: user.id,
          type: 'system',
          title,
          message,
          priority,
        });
      }
    },
    onSuccess: () => {
      setSelectedUsers([]);
      setTitle('');
      setMessage('');
      setPriority('normal');
      setActiveTab('inbox');
      qc.invalidateQueries({ queryKey: ['bff-notifications'] });
      showToast('Notification sent');
    },
  });

  const markReadMut = useMutation({
    mutationFn: (id: string) => bffNotificationService.markAsRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bff-notifications'] }),
  });

  const markAllMut = useMutation({
    mutationFn: () => bffNotificationService.markAllAsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bff-notifications'] });
      showToast('All notifications marked as read');
    },
  });

  const notifications = notifData?.notifications ?? [];
  const filteredNotifications = useMemo(() => {
    if (filter === 'unread') return notifications.filter((n: any) => !n.is_read);
    return notifications;
  }, [notifications, filter]);
  const unreadCount = notifications.filter((n: any) => !n.is_read).length;
  const groups = groupByDate(filteredNotifications);

  const showToast = (message: string) => {
    setToast({ open: true, message });
  };

  const handleSend = () => {
    if (selectedUsers.length > 0 && title && message) {
      sendMut.mutate();
    }
  };

  const handleNotificationClick = (notif: any) => {
    setReading(notif);
    if (!notif.is_read) {
      markReadMut.mutate(notif.id);
    }
  };

  const handleMarkAllRead = () => {
    if (unreadCount > 0) {
      markAllMut.mutate();
    }
  };

  const getSenderInfo = (notif: any) => {
    if (notif.sender_name) {
      const match = notif.sender_name.match(/^(.+?)\s*\(([^)]+)\)$/);
      if (match) return { name: match[1], role: match[2] };
      return { name: notif.sender_name, role: '' };
    }
    return null;
  };

  const canSend =
    selectedUsers.length > 0 &&
    title.trim() &&
    message.trim() &&
    message.length <= MESSAGE_MAX_LENGTH;

  const readingSender = reading ? getSenderInfo(reading) : null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: PANEL_WIDTH,
            maxWidth: '100%',
            bgcolor: '#FFFFFF',
            boxShadow: '-8px 0 24px rgba(28,35,33,0.06)',
            borderLeft: 'none',
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'rgba(28,35,33,0.28)',
            },
          },
        }}
        transitionDuration={{ enter: 260, exit: 200 }}
        sx={{
          '& .MuiDrawer-paper': {
            borderRadius: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ── Panel Head ── */}
          <Box sx={{ px: '22px', pt: '22px', pb: 0, flexShrink: 0, borderBottom: BORDER }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '18px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '22px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: INK,
                }}
              >
                Notifications
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '4px',
                  color: INK_SOFT,
                  '&:hover': { bgcolor: SURFACE_SUNK, color: INK },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* ── Segmented Tabs ── */}
            <Box
              sx={{
                display: 'flex',
                background: SURFACE_SUNK,
                borderRadius: '6px',
                padding: '3px',
                gap: '3px',
                mb: '18px',
              }}
            >
              <Box
                component="button"
                onClick={() => setActiveTab('inbox')}
                sx={{
                  flex: 1,
                  border: 'none',
                  background: activeTab === 'inbox' ? '#FFFFFF' : 'transparent',
                  boxShadow: activeTab === 'inbox' ? '0 1px 2px rgba(28,35,33,0.08)' : 'none',
                  padding: '8px 10px',

                  fontSize: '13px',
                  fontWeight: 600,
                  color: activeTab === 'inbox' ? INK : INK_SOFT,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                }}
              >
                Inbox
                {unreadCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: '"DM Mono", monospace',
                      fontSize: '10.5px',
                      background: activeTab === 'inbox' ? ACCENT : RUST,
                      color: '#fff',
                      padding: '1px 5px',
                      borderRadius: '8px',
                      lineHeight: '14px',
                    }}
                  >
                    {unreadCount}
                  </Box>
                )}
              </Box>
              <Box
                component="button"
                onClick={() => setActiveTab('compose')}
                sx={{
                  flex: 1,
                  border: 'none',
                  background: activeTab === 'compose' ? '#FFFFFF' : 'transparent',
                  boxShadow: activeTab === 'compose' ? '0 1px 2px rgba(28,35,33,0.08)' : 'none',
                  padding: '8px 10px',

                  fontSize: '13px',
                  fontWeight: 600,
                  color: activeTab === 'compose' ? INK : INK_SOFT,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                }}
              >
                <AddIcon sx={{ fontSize: '13px' }} />
                New notification
              </Box>
            </Box>
          </Box>

          {/* ── Inbox View ── */}
          {activeTab === 'inbox' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              {/* Toolbar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '22px',
                  height: 40,
                  flexShrink: 0,
                  borderBottom: BORDER,
                }}
              >
                <Box sx={{ display: 'flex', gap: '6px' }}>
                  {(['all', 'unread'] as Filter[]).map(f => (
                    <Box
                      key={f}
                      component="button"
                      onClick={() => setFilter(f)}
                      sx={{
                        border: BORDER,
                        background: filter === f ? INK : '#FFFFFF',
                        color: filter === f ? '#fff' : INK_SOFT,
                        fontSize: '12px',
                        fontWeight: 500,
                        padding: '5px 10px',
                        borderRadius: '20px',
                        cursor: 'pointer',

                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: filter === f ? INK : '#D6D2C6',
                        },
                      }}
                    >
                      {f === 'all' ? 'All' : 'Unread'}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: '14px' }}>
                  <Box
                    component="button"
                    onClick={handleMarkAllRead}
                    disabled={unreadCount === 0}
                    sx={{
                      background: 'none',
                      border: 'none',

                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: unreadCount > 0 ? INK_SOFT : INK_FAINT,
                      cursor: unreadCount > 0 ? 'pointer' : 'default',
                      padding: 0,
                      '&:hover': unreadCount > 0 ? { color: ACCENT } : {},
                    }}
                  >
                    Mark all read
                  </Box>
                  <Box
                    component="button"
                    onClick={handleMarkAllRead}
                    sx={{
                      background: 'none',
                      border: 'none',

                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: INK_SOFT,
                      cursor: 'pointer',
                      padding: 0,
                      '&:hover': { color: RUST },
                    }}
                  >
                    Clear all
                  </Box>
                </Box>
              </Box>

              {/* Notification List */}
              <Box sx={{ flex: 1, overflow: 'auto', ...thinScrollbar }}>
                {notifLoading && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}
                  >
                    <CircularProgress sx={{ color: ACCENT, size: 28 }} />
                  </Box>
                )}

                {!notifLoading && groups.length === 0 && (
                  <Box sx={{ py: 8, textAlign: 'center', px: '30px' }}>
                    <NotificationsIcon
                      sx={{ fontSize: 34, color: INK_FAINT, opacity: 0.5, mb: '12px' }}
                    />
                    <Typography
                      sx={{
                        fontSize: '13.5px',
                        color: INK_FAINT,
                      }}
                    >
                      {filter === 'unread' ? "You're all caught up." : 'No notifications yet.'}
                    </Typography>
                  </Box>
                )}

                {groups.map(group => (
                  <React.Fragment key={group.label}>
                    <Box
                      sx={{
                        padding: '14px 22px 8px 22px',
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: INK_FAINT,
                      }}
                    >
                      {group.label}
                    </Box>
                    {group.items.map((notif: any) => {
                      const cat = getCategory(notif.type);
                      const sender = getSenderInfo(notif);
                      const codeLabel = notif.resource_id
                        ? notif.resource_type
                          ? `${notif.resource_type.toUpperCase()}-${String(notif.resource_id).slice(0, 8)}`
                          : notif.resource_id
                        : null;

                      return (
                        <Box
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            gap: '12px',
                            padding: '14px 22px 14px 18px',
                            borderBottom: BORDER,
                            cursor: 'pointer',
                            bgcolor: notif.is_read ? '#FFFFFF' : '#FBFAF7',
                            transition: 'background 0.12s ease',
                            '&:hover': {
                              bgcolor: SURFACE_SUNK,
                              '& .notif-delete': { opacity: 1 },
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '3px',
                              background: cat.color,
                            },
                          }}
                        >
                          {/* Icon */}
                          <Box
                            sx={{
                              flexShrink: 0,
                              width: 34,
                              height: 34,
                              borderRadius: '4px',
                              background: cat.tint,
                              color: cat.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '& svg': { fontSize: '17px' },
                            }}
                          >
                            {cat.icon}
                          </Box>

                          {/* Body */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '8px',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: INK,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                }}
                              >
                                {!notif.is_read && (
                                  <Box
                                    component="span"
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      background: RUST,
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                                {notif.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: '"DM Mono", monospace',
                                  fontSize: '11px',
                                  color: INK_FAINT,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                  mt: '2px',
                                }}
                              >
                                {timeAgo(notif.created_at)}
                              </Typography>
                            </Box>

                            <Typography
                              sx={{
                                fontSize: '13px',
                                color: INK_SOFT,
                                lineHeight: 1.45,
                                mt: '3px',
                              }}
                            >
                              {notif.message}
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                mt: '8px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {codeLabel && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontFamily: '"DM Mono", monospace',
                                    fontSize: '10.5px',
                                    background: SURFACE_SUNK,
                                    border: BORDER,
                                    color: INK_SOFT,
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {codeLabel}
                                </Box>
                              )}
                              {(notif.priority === 'high' || notif.priority === 'urgent') && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: '10.5px',
                                    fontWeight: 600,
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    background:
                                      notif.priority === 'urgent' ? RUST_TINT : AMBER_TINT,
                                    color: notif.priority === 'urgent' ? RUST : AMBER,
                                  }}
                                >
                                  {notif.priority}
                                </Box>
                              )}
                              {sender && (
                                <Typography sx={{ fontSize: '11px', color: INK_FAINT }}>
                                  {sender.name}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Delete button (hover) */}
                          <Box
                            className="notif-delete"
                            onClick={e => {
                              e.stopPropagation();
                              markReadMut.mutate(notif.id);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 16,
                              opacity: 0,
                              width: 26,
                              height: 26,
                              borderRadius: '4px',
                              border: 'none',
                              background: '#FFFFFF',
                              color: INK_FAINT,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition:
                                'opacity 0.12s ease, color 0.12s ease, background 0.12s ease',
                              '&:hover': { background: RUST_TINT, color: RUST },
                              '& svg': { fontSize: '14px' },
                            }}
                          >
                            <DeleteOutlineIcon />
                          </Box>
                        </Box>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Compose View ── */}
          {activeTab === 'compose' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Box
                sx={{ flex: 1, overflow: 'auto', padding: '20px 22px 24px 22px', ...thinScrollbar }}
              >
                {/* Recipients */}
                <Box sx={{ mb: '18px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: INK_SOFT,
                      mb: '7px',
                    }}
                  >
                    To
                  </Typography>
                  <Autocomplete
                    multiple
                    options={recipientsData?.recipients ?? []}
                    value={selectedUsers}
                    onChange={(_, v) => setSelectedUsers(v)}
                    getOptionLabel={option => `${option.first_name} ${option.last_name}`}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Add staff or a department..."
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '13.5px',

                            '& fieldset': { borderColor: '#E4E1D8' },
                            '&:hover fieldset': { borderColor: '#D6D2C6' },
                            '&.Mui-focused fieldset': { borderColor: ACCENT },
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={`${option.first_name} ${option.last_name?.[0]}.`}
                          size="small"
                          {...getTagProps({ index })}
                          key={option.id}
                          sx={{
                            borderRadius: '20px',
                            background: PINE_TINT,
                            color: ACCENT,
                            fontSize: '12.5px',
                            fontWeight: 500,

                            '& .MuiChip-deleteIcon': {
                              color: ACCENT,
                              opacity: 0.7,
                              fontSize: '16px',
                            },
                          }}
                        />
                      ))
                    }
                    sx={{ mb: '6px' }}
                  />
                  <Typography
                    sx={{
                      fontSize: '11.5px',
                      color: INK_FAINT,
                    }}
                  >
                    Select individuals or entire departments to notify.
                  </Typography>
                </Box>

                {/* Subject */}
                <Box sx={{ mb: '18px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: INK_SOFT,
                      mb: '7px',
                    }}
                  >
                    Subject
                  </Typography>
                  <TextField
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Pool maintenance 2–4pm today"
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '13.5px',

                        '& fieldset': { borderColor: '#E4E1D8' },
                        '&:hover fieldset': { borderColor: '#D6D2C6' },
                        '&.Mui-focused fieldset': { borderColor: ACCENT },
                      },
                    }}
                  />
                </Box>

                {/* Message */}
                <Box sx={{ mb: '18px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: INK_SOFT,
                      mb: '7px',
                    }}
                  >
                    Message
                  </Typography>
                  <TextField
                    value={message}
                    onChange={e => {
                      if (e.target.value.length <= MESSAGE_MAX_LENGTH) {
                        setMessage(e.target.value);
                      }
                    }}
                    placeholder="Write the notification message..."
                    multiline
                    rows={4}
                    fullWidth
                    inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '13.5px',

                        '& fieldset': { borderColor: '#E4E1D8' },
                        '&:hover fieldset': { borderColor: '#D6D2C6' },
                        '&.Mui-focused fieldset': { borderColor: ACCENT },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mt: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"DM Mono", monospace',
                        fontSize: '11px',
                        color:
                          message.length > MESSAGE_MAX_LENGTH * 0.9
                            ? RUST
                            : message.length > MESSAGE_MAX_LENGTH * 0.7
                              ? AMBER
                              : INK_FAINT,
                      }}
                    >
                      {message.length}/{MESSAGE_MAX_LENGTH}
                    </Typography>
                  </Box>
                </Box>

                {/* Priority */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: INK_SOFT,
                      mb: '7px',
                    }}
                  >
                    Priority
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    {(['low', 'normal', 'high', 'urgent'] as const).map(p => {
                      const isActive = priority === p;
                      let borderColor = '#E4E1D8';
                      let bg = '#FFFFFF';
                      let color = INK_SOFT;
                      if (isActive) {
                        if (p === 'low') {
                          borderColor = GRAY;
                          bg = SURFACE_SUNK;
                          color = INK;
                        }
                        if (p === 'normal') {
                          borderColor = ACCENT;
                          bg = PINE_TINT;
                          color = ACCENT;
                        }
                        if (p === 'high') {
                          borderColor = AMBER;
                          bg = AMBER_TINT;
                          color = AMBER;
                        }
                        if (p === 'urgent') {
                          borderColor = RUST;
                          bg = RUST_TINT;
                          color = RUST;
                        }
                      }
                      return (
                        <Box
                          key={p}
                          component="button"
                          onClick={() => setPriority(p)}
                          sx={{
                            flex: 1,
                            border: `1px solid ${borderColor}`,
                            background: bg,
                            borderRadius: '6px',
                            padding: '9px 4px',
                            textAlign: 'center',
                            fontSize: '12.5px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            color,

                            transition: 'all 0.12s ease',
                            textTransform: 'capitalize',
                          }}
                        >
                          {p}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>

              {/* Compose Footer */}
              <Box
                sx={{
                  padding: '16px 22px',
                  borderTop: BORDER,
                  flexShrink: 0,
                  display: 'flex',
                  gap: '10px',
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    sendMut.isPending ? (
                      <CircularProgress size={15} sx={{ color: '#fff' }} />
                    ) : (
                      <SendOutlinedIcon />
                    )
                  }
                  onClick={handleSend}
                  disabled={!canSend || sendMut.isPending}
                  sx={{
                    background: canSend ? ACCENT : '#D6D2C6',
                    color: canSend ? '#fff' : INK_FAINT,
                    borderRadius: '6px',
                    padding: '12px 16px',

                    fontSize: '13.5px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: canSend ? '#274F44' : '#D6D2C6', boxShadow: 'none' },
                    '&.Mui-disabled': { background: '#D6D2C6', color: INK_FAINT },
                  }}
                >
                  {sendMut.isPending ? 'SENDING...' : 'Send notification'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Toast ── */}
        <Snackbar
          open={toast.open}
          autoHideDuration={2200}
          onClose={() => setToast({ open: false, message: '' })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setToast({ open: false, message: '' })}
            severity="success"
            icon={<CheckCircleOutlineIcon />}
            sx={{
              borderRadius: '6px',

              fontSize: '13px',
              fontWeight: 500,
              bgcolor: INK,
              color: '#fff',
              '& .MuiAlert-icon': { color: '#8FCBB4' },
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Drawer>
      <Modal
        open={reading !== null}
        onClose={() => setReading(null)}
        title={reading?.title || 'Notification'}
        maxWidth="sm"
        showClose
        footer={
          <Button
            onClick={() => setReading(null)}
            sx={{ textTransform: 'none', color: ACCENT, fontWeight: 600 }}
          >
            Done
          </Button>
        }
      >
        {reading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '12px', color: INK_FAINT }}>
                {readingSender?.name?.trim() && /[A-Za-z0-9]/.test(readingSender.name)
                  ? `From ${readingSender.name}${readingSender.role ? ` · ${readingSender.role}` : ''}`
                  : 'System'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: INK_FAINT }}>
                {reading.created_at ? new Date(reading.created_at).toLocaleString() : ''}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '14px',
                color: INK,
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
              }}
            >
              {reading.message}
            </Typography>
          </Box>
        )}
      </Modal>
    </>
  );
}
